import { call, delay, put, takeLatest } from "redux-saga/effects";

import { startLoader, stopLoader } from "../../../../utils/commonFunctions";

// for toast
import { toast } from "react-toastify";
import { HrActionTypes, HrActions } from "../../../actionTypes/hr/hrActionTypes";
import {
  applyLeaveApplicationApi,
  applyRegularizationApi,
  createHolidayApi,
  editHolidayByIdApi,
  editLeaveApi,
  editRegularizationApi,
  getActivityApi,
  getAttendanceReportApi,
  getHolidayByIdApi,
  getHolidayListApi,
  getHrAttendanceListApi,
  getLeaveDataByIdApi,
  getLeaveListApi,
  getMyAttendanceListApi,
  getMyRegularizationListApi,
  getRegularizationByIdApi,
  getRegularizationListApi,
  userCheckOutApi,
  userCheckinApi,
} from "../../requests/hr/hrRequests";
import {
  getActivity,
  setActivityData,
  setDailyAttendanceList,
  setDailyAttendanceListCount,
  setHolidayData,
  setHolidayList,
  setHolidayListCount,
  setLeaveData,
  setLeaveList,
  setLeaveListCount,
  setMyDailyAttendanceList,
  setMyDailyAttendanceListCount,
  setMyRegularizationList,
  setMyRegularizationListCount,
  setRegularizationData,
  setRegularizationList,
  setRegularizationListCount,
  setUserCheckIn,
  setUserCheckOut,
} from "../../../actions/hr/hrActions";
import moment from "moment";
import { setPostApiStatus, setPutApiStatus } from "../../../actions/apiStatusActions";


function* getHrAttendanceListHandler(action: HrActionTypes) {
  yield delay(500); //to hit search api after 500ms of the last key stroke
  try {
    yield startLoader("transparent");
    const { data } = yield call(getHrAttendanceListApi, action.payload);
    if (data.status) {
      yield put(setDailyAttendanceList(data?.data?.rows));
      yield put(setDailyAttendanceListCount(Math.ceil(data?.data?.count / action.payload.pageLimit) || 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getMyAttendanceListHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getMyAttendanceListApi, action.payload);
    if (data.status) {
      const fallbackData = [
        {
          id: 0,
          user_id: 0,
          check_in: null,
          check_out: null,
          total_work_duration: null,
          overtime_hours: null,
          in_geo_location: null,
          out_geo_location: null,
          in_device_info: null,
          out_device_info: null,
          status: "-",
          is_late: null,
          approved: null,
          remarks: null,
          createdAt: null,
          updatedAt: null,
        },
      ];
      yield put(setMyDailyAttendanceList(data?.data?.rows || fallbackData));
      yield put(setMyDailyAttendanceListCount(Math.ceil(data?.data?.count / action.payload.pageLimit) || 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getMyRegularizationListHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getMyRegularizationListApi, action.payload);
    if (data.status) {
      const fallbackData = [
        {
          id: 0,
          user_id: 0,
          check_in: null,
          check_out: null,
          total_work_duration: null,
          overtime_hours: null,
          in_geo_location: null,
          out_geo_location: null,
          in_device_info: null,
          out_device_info: null,
          status: "-",
          is_late: null,
          approved: null,
          remarks: null,
          createdAt: null,
          updatedAt: null,
        },
      ];
      yield put(setMyRegularizationList(data?.data?.rows || fallbackData));
      yield put(setMyRegularizationListCount(Math.ceil(data?.data?.count / action.payload.pageLimit) || 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getRegularizationListHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getRegularizationListApi, action.payload);
    if (data.status) {
      const fallbackData = [
        {
          id: 0,
          employee_id: 0,
          check_in: null,
          check_out: null,
          status: "-",
          attendance_id: null,
          reason: "-",
          remark: "-",
          work_duration: null,
          createdAt: null,
          updatedAt: null,
        },
      ];
      yield put(setRegularizationList(data?.data?.rows || fallbackData));
      yield put(setRegularizationListCount(Math.ceil(data?.data?.count / action.payload.pageLimit) || 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getAttendanceReportHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getAttendanceReportApi, action.payload);
    // on success, opening the link in new window
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance Report ${action.payload.from_date} - ${action.payload.to_date}`;
      a.click();
      URL.revokeObjectURL(url);
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* userCheckInHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(userCheckinApi, action.payload);

    if (data.status) {
      // toast message
      toast.success(data?.message, {
        position: "top-right",
      });
      yield put(setUserCheckIn(true));
      yield put(getActivity({ date: moment(new Date()).format("YYYY-MM-DD") }));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setUserCheckIn(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* userCheckOutHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(userCheckOutApi, action.payload);

    if (data.status) {
      // toast message
      toast.success(data?.message, {
        position: "top-right",
      });
      yield put(setUserCheckOut(true));
      yield put(getActivity({ date: moment(new Date()).format("YYYY-MM-DD") }));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setUserCheckOut(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* getActivityHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getActivityApi, action.payload);

    if (data.status) {
      yield put(setActivityData(data.data));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getLeaveListHandler(action: HrActionTypes) {
  if (action.payload.search) {
    yield delay(500); //to hit search api after 500ms of the last key stroke
  }
  try {
    yield startLoader("transparent");
    const { data } = yield call(getLeaveListApi, action.payload);
    if (data.status) {
      const fallbackData = [
        {
          id: 0,
          status: "-",
          // from_date: "2023-10-31T18:30:00.000Z",
          // to_date: "2023-11-08T18:30:00.000Z",
          employee_id: 0,
          remarks: "-",
          reason: "-",
          application_logs: [
            {
              created_by: "-",
              // time_stamp: "2023-11-02T19:57:49+05:30",
            },
          ],
          created_by: 0,
          application_user: {
            id: 0,
            name: "-",
            branch: {
              id: 0,
              hub: {
                id: 0,
              },
            },
          },
        },
      ];
      yield put(setLeaveList(data?.data?.rows || fallbackData));
      yield put(setLeaveListCount(data?.data?.count ?? 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getLeaveByIdHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getLeaveDataByIdApi, action.payload);
    if (data.status) {
      yield put(setLeaveData(data?.data));
    } else {
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* editLeaveHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(editLeaveApi, action.payload);
    if (data.status) {
      toast.success(data.message, {
        position: "top-right",
      });
      yield put(setPutApiStatus(true));
      // yield put(getBranchList({ page: 0, pageLimit: 10 }));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setPutApiStatus(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* applyLeaveApplicationHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(applyLeaveApplicationApi, action.payload);
    if (data.status) {
      toast.success(data.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* applyRegularizationHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(applyRegularizationApi, action.payload.data);
    if (data.status) {
      toast.success(data.message, {
        position: "top-right",
      });

      // closing modal after successfull response
      action.payload.handleClose();
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* getRegularizationByIdListHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getRegularizationByIdApi, action.payload);
    if (data.status) {
      yield put(setRegularizationData(data?.data));
    } else {
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* editRegularizationHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(editRegularizationApi, action.payload);
    if (data.status) {
      toast.success(data.message, {
        position: "top-right",
      });
      yield put(setPutApiStatus(true));
      // yield put(getBranchList({ page: 0, pageLimit: 10 }));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setPutApiStatus(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* getHolidayListHandler(action: HrActionTypes) {
  if (action.payload.search) {
    yield delay(500);
  }
  try {
    yield startLoader("transparent");
    const { data } = yield call(getHolidayListApi, action.payload);
    if (data.status) {
      const fallbackData = [
        {
          id: 0,
          holiday_name: "-",
          holiday_date: null,
          holiday_remark: "-",
          createdAt: null,
          updatedAt: null,
        },
      ];
      yield put(setHolidayList(data?.data?.rows || fallbackData));
      yield put(setHolidayListCount(Math.ceil(data?.data?.count / action.payload.pageLimit) || 0));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* createHolidayHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(createHolidayApi, action.payload);

    if (data.status) {
      // toast message
      toast.success(data?.message, {
        position: "top-right",
      });
      yield put(setPostApiStatus(true));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setPostApiStatus(false));
  } finally {
    yield stopLoader("transparent");
  }
}

function* getHolidayByIdHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(getHolidayByIdApi, action.payload);
    if (data.status) {
      yield put(setHolidayData(data?.data));
    } else {
      toast.error(data?.message, {
        position: "top-right",
      });
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
  } finally {
    yield stopLoader("transparent");
  }
}

function* editHolidayByIdHandler(action: HrActionTypes) {
  try {
    yield startLoader("transparent");
    const { data } = yield call(editHolidayByIdApi, action.payload);
    if (data.status) {
      toast.success(data.message, {
        position: "top-right",
      });
      yield put(setPutApiStatus(true));
      // yield put(getHolidayList({ page: 0, pageLimit: 10 }));
    }
  } catch (error: any) {
    toast.error(error?.response?.data?.message, {
      position: "top-right",
    });
    yield put(setPutApiStatus(false));
  } finally {
    yield stopLoader("transparent");
  }
}

// Hr sagas
export default function* hrSaga() {
  yield takeLatest(HrActions.GET_DAILY_ATTENDANCE_LIST, getHrAttendanceListHandler);
  yield takeLatest(HrActions.GET_MY_DAILY_ATTENDANCE_LIST, getMyAttendanceListHandler);
  yield takeLatest(HrActions.GET_ATTENDANCE_REPORT, getAttendanceReportHandler);
  yield takeLatest(HrActions.USER_CHECK_IN, userCheckInHandler);
  yield takeLatest(HrActions.USER_CHECK_OUT, userCheckOutHandler);
  yield takeLatest(HrActions.GET_ACTIVITY, getActivityHandler);
  yield takeLatest(HrActions.GET_LEAVE_LIST, getLeaveListHandler);
  yield takeLatest(HrActions.GET_LEAVE_BY_ID, getLeaveByIdHandler);
  yield takeLatest(HrActions.EDIT_LEAVE, editLeaveHandler);
  yield takeLatest(HrActions.APPLY_LEAVE, applyLeaveApplicationHandler);
  yield takeLatest(HrActions.APPLY_REGULARIZATION, applyRegularizationHandler);
  yield takeLatest(HrActions.GET_MY_REGULARIZATION_LIST, getMyRegularizationListHandler);
  yield takeLatest(HrActions.GET_REGULARIZATION_LIST, getRegularizationListHandler);
  yield takeLatest(HrActions.GET_REGULARIZATION_BY_ID, getRegularizationByIdListHandler);
  yield takeLatest(HrActions.EDIT_REGULARIZATION, editRegularizationHandler);
  yield takeLatest(HrActions.GET_HOLIDAY_LIST, getHolidayListHandler);
  yield takeLatest(HrActions.CREATE_HOLIDAY, createHolidayHandler);
  yield takeLatest(HrActions.GET_HOLIDAY_BY_ID, getHolidayByIdHandler);
  yield takeLatest(HrActions.EDIT_HOLIDAY, editHolidayByIdHandler);
}
